import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Loader2, User, Bell, Eye, Key, HelpCircle, Trash2, Database, ArrowLeft } from "lucide-react";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { DataExportSection } from "@/components/settings/DataExportSection";
import { ProcessingObjectionsSection } from "@/components/settings/ProcessingObjectionsSection";
import { AccountRestrictionSection } from "@/components/settings/AccountRestrictionSection";
import { Alert, AlertDescription } from "@/components/ui/alert";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";

const Settings = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savingPassword, setSavingPassword] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const { 
    preferences, 
    loading: preferencesLoading, 
    saving, 
    updatePreference, 
    updatePreferences,
    restrictAccount,
    unrestrictAccount
  } = useUserPreferences();

  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    if (!user) {
      navigate("/auth", { replace: true });
      return;
    }

    if (user.email) {
      setUserEmail(user.email);
    }
    
    setLoading(false);
  }, [user, navigate]);

  const handleNotificationToggle = async (key: string) => {
    const preferenceKey = key === 'new-papers' ? 'notify_new_papers' 
      : key === 'comments' ? 'notify_comments'
      : key === 'mentions' ? 'notify_mentions'
      : key === 'ai-review-ready' ? 'notify_ai_review_ready'
      : 'notify_updates';
    
    const currentValue = preferences?.[preferenceKey as keyof typeof preferences] as boolean ?? true;
    const success = await updatePreference(preferenceKey as any, !currentValue);
    
    if (success) {
      toast.success('Notification setting updated');
    }
  };

  const handlePrivacyToggle = async (key: string) => {
    const preferenceKey = key === 'profile-visibility' ? 'profile_visibility'
      : key === 'show-email' ? 'show_email'
      : key === 'allow-mentions' ? 'allow_mentions'
      : 'allow_search_indexing';
    
    const currentValue = preferences?.[preferenceKey as keyof typeof preferences] as boolean ?? true;
    const success = await updatePreference(preferenceKey as any, !currentValue);
    
    if (success) {
      toast.success('Privacy setting updated');
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    
    if (formData.newPassword.length < 6) {
      toast.error("Password should be at least 6 characters");
      return;
    }
    
    try {
      setSavingPassword(true);
      
      const { error } = await supabase.auth.updateUser({
        password: formData.newPassword
      });
      
      if (error) throw error;
      
      toast.success("Password updated successfully");
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
    } catch (error: any) {
      console.error("Error updating password:", error);
      toast.error(error.message || "Failed to update password");
    } finally {
      setSavingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE") {
      toast.error("Please type DELETE to confirm account deletion");
      return;
    }

    if (!user) return;

    try {
      setSavingPassword(true);
      
      const { data, error } = await supabase.functions.invoke('delete-account', {
        body: { userId: user.id }
      });

      if (error) {
        console.error('Account deletion error:', error);
        throw new Error(error.message || 'Failed to delete account');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to delete account');
      }

      toast.success('Your account has been permanently deleted');

      await signOut();
      navigate("/");
    } catch (error: any) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account', {
        description: error.message || 'Please try again or contact support'
      });
    } finally {
      setSavingPassword(false);
      setDeleteDialogOpen(false);
      setDeleteConfirmText("");
    }
  };

  const notificationSettings = [
    {
      id: "ai-review-ready",
      title: "AI review ready",
      description: "Email me when an AI review finishes for one of my papers",
      enabled: preferences?.notify_ai_review_ready ?? true,
    },
    {
      id: "new-papers",
      title: "New Papers",
      description: "Get notified when papers related to your interests are uploaded",
      enabled: preferences?.notify_new_papers ?? true,
    },
    {
      id: "comments",
      title: "Comments",
      description: "Get notified when someone comments on your papers",
      enabled: preferences?.notify_comments ?? true,
    },
    {
      id: "mentions",
      title: "Mentions",
      description: "Get notified when someone mentions you in a comment",
      enabled: preferences?.notify_mentions ?? true,
    },
    {
      id: "updates",
      title: "Updates",
      description: "Get notified about platform updates and new features",
      enabled: preferences?.notify_updates ?? false,
    },
  ];

  const privacySettings = [
    {
      id: "profile-visibility",
      title: "Public Profile",
      description: "Make your profile visible to anyone",
      enabled: preferences?.profile_visibility ?? true,
    },
    {
      id: "show-email",
      title: "Show Email",
      description: "Show your email address on your public profile",
      enabled: preferences?.show_email ?? false,
    },
    {
      id: "allow-mentions",
      title: "Allow Mentions",
      description: "Let other users mention you in comments",
      enabled: preferences?.allow_mentions ?? true,
    },
    {
      id: "search-indexing",
      title: "Search Indexing",
      description: "Allow your profile and papers to be indexed by search engines",
      enabled: preferences?.allow_search_indexing ?? true,
    },
  ];

  if (loading || preferencesLoading) {
    return (
      <>
        <Helmet>
          <title>Settings - PaperVerse</title>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <div className="min-h-screen flex flex-col">
          <Navbar />
          <main className="flex-grow container max-w-4xl mx-auto pt-32 pb-16 px-4">
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
                <h2 className="text-xl font-medium">Loading settings...</h2>
              </div>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Settings - PaperVerse</title>
        <meta name="description" content="Manage your account settings and preferences" />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow container max-w-4xl mx-auto pt-32 pb-16 px-4">
          <div className="mb-6">
            <Link to="/">
              <Button variant="ghost" size="sm" className="gap-1">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Button>
            </Link>
          </div>

          {/* Account Restriction Banner */}
          {preferences?.account_restricted && (
            <Alert className="mb-6 border-amber-500 bg-amber-50 dark:bg-amber-900/20">
              <AlertDescription className="text-amber-800 dark:text-amber-200">
                <strong>Your account is currently restricted.</strong> Processing has been limited 
                to essential storage only. Go to Your Data tab to remove restrictions.
              </AlertDescription>
            </Alert>
          )}

          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Settings</h1>
            <p className="text-muted-foreground">
              Manage your account settings and preferences
            </p>
          </div>

          <Tabs defaultValue="account" className="space-y-4">
            <TabsList className="grid grid-cols-4 md:w-[400px]">
              <TabsTrigger value="account" className="flex items-center gap-1">
                <User className="h-4 w-4 md:mr-1" />
                <span className="hidden md:inline">Account</span>
              </TabsTrigger>
              <TabsTrigger value="notifications" className="flex items-center gap-1">
                <Bell className="h-4 w-4 md:mr-1" />
                <span className="hidden md:inline">Notifications</span>
              </TabsTrigger>
              <TabsTrigger value="privacy" className="flex items-center gap-1">
                <Eye className="h-4 w-4 md:mr-1" />
                <span className="hidden md:inline">Privacy</span>
              </TabsTrigger>
              <TabsTrigger value="data" className="flex items-center gap-1">
                <Database className="h-4 w-4 md:mr-1" />
                <span className="hidden md:inline">Your Data</span>
              </TabsTrigger>
            </TabsList>

            {/* Account Settings (merged General + Security) */}
            <TabsContent value="account" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Account Information</CardTitle>
                  <CardDescription>
                    Manage your account details
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      value={userEmail}
                      readOnly
                      className="bg-muted/50"
                    />
                    <p className="text-xs text-muted-foreground">
                      Your email address cannot be changed
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="language">Language</Label>
                    <select
                      id="language"
                      className="w-full p-2 border rounded-md bg-background"
                      defaultValue="en"
                    >
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timezone">Time Zone</Label>
                    <select
                      id="timezone"
                      className="w-full p-2 border rounded-md bg-background"
                      defaultValue="UTC"
                    >
                      <option value="UTC">UTC</option>
                      <option value="EST">Eastern Time (EST)</option>
                      <option value="CST">Central Time (CST)</option>
                      <option value="MST">Mountain Time (MST)</option>
                      <option value="PST">Pacific Time (PST)</option>
                    </select>
                  </div>
                </CardContent>
                <CardFooter className="flex justify-end">
                  <Button 
                    onClick={() => {
                      toast.success("Settings saved successfully");
                    }}
                  >
                    Save Changes
                  </Button>
                </CardFooter>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Change Password</CardTitle>
                  <CardDescription>
                    Update your account password
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <form onSubmit={handlePasswordChange} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="currentPassword">Current Password</Label>
                      <Input
                        id="currentPassword"
                        type="password"
                        value={formData.currentPassword}
                        onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="newPassword">New Password</Label>
                      <Input
                        id="newPassword"
                        type="password"
                        value={formData.newPassword}
                        onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirm New Password</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        required
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full"
                      disabled={savingPassword}
                    >
                      {savingPassword ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        <>
                          <Key className="h-4 w-4 mr-2" />
                          Update Password
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Subscription</CardTitle>
                  <CardDescription>
                    Cancel your PaperVerse subscription at any time. No login is required for this —
                    the same form is always available in the footer.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild variant="outline">
                    <Link to="/cancel-contract">Cancel contract here</Link>
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Notification Settings */}
            <TabsContent value="notifications">
              <Card>
                <CardHeader>
                  <CardTitle>Notification Settings</CardTitle>
                  <CardDescription>
                    Manage how you receive notifications
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-4">
                    {notificationSettings.map((setting) => (
                      <div key={setting.id} className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <h4 className="text-sm font-medium">{setting.title}</h4>
                          <p className="text-xs text-muted-foreground">{setting.description}</p>
                        </div>
                        <Switch
                          checked={setting.enabled}
                          onCheckedChange={() => handleNotificationToggle(setting.id)}
                          disabled={saving}
                        />
                      </div>
                    ))}
                  </div>

                  <Separator className="my-4" />

                  <div className="space-y-2">
                    <Label htmlFor="emailFrequency">Email Frequency</Label>
                    <select
                      id="emailFrequency"
                      className="w-full p-2 border rounded-md bg-background"
                      value={preferences?.email_frequency ?? 'daily'}
                      onChange={async (e) => {
                        const success = await updatePreference('email_frequency', e.target.value);
                        if (success) toast.success('Email frequency updated');
                      }}
                      disabled={saving}
                    >
                      <option value="immediate">Immediate</option>
                      <option value="daily">Daily Digest</option>
                      <option value="weekly">Weekly Digest</option>
                      <option value="never">Never</option>
                    </select>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Privacy Settings */}
            <TabsContent value="privacy">
              <Card>
                <CardHeader>
                  <CardTitle>Privacy Settings</CardTitle>
                  <CardDescription>
                    Control your profile visibility
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-4">
                    {privacySettings.map((setting) => (
                      <div key={setting.id} className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <h4 className="text-sm font-medium">{setting.title}</h4>
                          <p className="text-xs text-muted-foreground">{setting.description}</p>
                        </div>
                        <Switch
                          checked={setting.enabled}
                          onCheckedChange={() => handlePrivacyToggle(setting.id)}
                          disabled={saving}
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Your Data Tab (GDPR Rights) */}
            <TabsContent value="data" className="space-y-6">
              <DataExportSection />
              <ProcessingObjectionsSection 
                preferences={preferences}
                saving={saving}
                onUpdate={updatePreference}
              />
              <AccountRestrictionSection
                preferences={preferences}
                saving={saving}
                onRestrict={restrictAccount}
                onUnrestrict={unrestrictAccount}
              />
              
              {/* Delete Account - moved from Security */}
              <Card className="border-destructive/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-destructive">
                    <Trash2 className="h-5 w-5" />
                    Delete Account
                  </CardTitle>
                  <CardDescription>
                    Permanently delete your account and all data
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    This will permanently remove your profile, papers, comments, reviews, and all associated files. 
                    This action cannot be undone.
                  </p>

                  <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="destructive" className="w-full">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete My Account
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Permanently Delete Account</DialogTitle>
                        <DialogDescription className="space-y-3 pt-2 text-left">
                          <p className="font-semibold text-destructive">This action cannot be undone and is non-recoverable. There is no soft-delete or grace period.</p>

                          <div className="space-y-1 text-sm">
                            <p className="font-medium text-foreground">What will be permanently deleted:</p>
                            <ul className="list-disc pl-5 space-y-0.5 text-muted-foreground">
                              <li>Your profile, name, bio and contact details</li>
                              <li>All papers you uploaded as sole author, plus their files in storage</li>
                              <li>Your comments, replies, votes and reviews</li>
                              <li>Your subscription, role assignments and notification preferences</li>
                              <li>Your consent records (signup, AI, purchases)</li>
                            </ul>
                          </div>

                          <div className="space-y-1 text-sm">
                            <p className="font-medium text-foreground">What will be anonymised (kept without link to you):</p>
                            <ul className="list-disc pl-5 space-y-0.5 text-muted-foreground">
                              <li>Aggregated usage statistics (user_id set to NULL)</li>
                              <li>Credit purchase records — retained 10 years per § 147 AO / § 257 HGB (German tax law); user_id set to NULL</li>
                            </ul>
                          </div>

                          <p className="text-xs text-muted-foreground pt-1">
                            Your right to erasure under Art. 17 GDPR is fulfilled. The two anonymised categories above no longer identify you (Recital 26 GDPR).
                          </p>
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-3 py-4">
                        <p className="text-sm">
                          Type <strong>DELETE</strong> to confirm:
                        </p>
                        <Input
                          value={deleteConfirmText}
                          onChange={(e) => setDeleteConfirmText(e.target.value)}
                          placeholder="Type DELETE"
                          className="font-mono"
                        />
                      </div>
                      <DialogFooter>
                        <Button 
                          variant="outline" 
                          onClick={() => {
                            setDeleteDialogOpen(false);
                            setDeleteConfirmText("");
                          }}
                        >
                          Cancel
                        </Button>
                        <Button 
                          variant="destructive" 
                          onClick={handleDeleteAccount}
                          disabled={savingPassword || deleteConfirmText !== "DELETE"}
                        >
                          {savingPassword ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Deleting...
                            </>
                          ) : (
                            'Delete My Account'
                          )}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>

              <p className="text-xs text-center text-muted-foreground">
                <Link to="/ai-transparency" className="underline hover:text-foreground">
                  Learn more about your data rights
                </Link>
              </p>
            </TabsContent>
          </Tabs>

          <div className="mt-8 text-center">
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
              <HelpCircle className="h-4 w-4 mr-2" />
              Need help with your settings?
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Settings;
